import React, { useState } from 'react';
import { Dropdown, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useInvoice } from '../contexts/InvoiceContext';
import { Invoice, INVOICE_STATUS_CONFIG } from '../../../types/accounting';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { InvoicePdfActions } from '../../../components/InvoicePdfActions';
import { RecordPaymentModal } from './RecordPaymentModal';
import { PaymentHistory } from './PaymentHistory';
import { formatDateAU, isDateOverdue } from '../../../utils/dateUtils';

const formatDateStr = formatDateAU;

interface InvoiceViewModalProps {
  show: boolean;
  invoice: Invoice | null;
  onHide: () => void;
  onVoided?: () => void;
  /**
   * Optional hook to duplicate the current invoice as a new draft. When
   * provided, renders a "Copy to New" action that hands the invoice back
   * to the host so it can launch CreateInvoiceModal in template mode.
   * Host is responsible for closing this modal if desired.
   */
  onCopyToNew?: (invoice: Invoice) => void;
  /**
   * Optional hook to edit the current invoice. When provided, renders
   * an "Edit" action (gated to non-PAID, non-VOIDED invoices — those
   * statuses are frozen in the accounting layer). Host launches
   * CreateInvoiceModal in edit mode with the invoice pre-filled.
   */
  onEdit?: (invoice: Invoice) => void;
}

export function InvoiceViewModal({ show, invoice, onHide, onVoided, onCopyToNew, onEdit }: InvoiceViewModalProps) {
  const { voidInvoice, emailInvoice, getOnlineInvoiceUrl, invoiceActionLoading, fetchPayments, deletePayment, paymentsLoading, deletePaymentLoading, recordPayment, recordPaymentLoading } = useInvoice();
  const status = invoice?.status ? INVOICE_STATUS_CONFIG[invoice.status] : null;
  const [onlineUrl, setOnlineUrl] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  // Bumped after a payment is recorded so the embedded PaymentHistory re-fetches
  // and the open view never shows a stale list (audit: low).
  const [paymentReloadKey, setPaymentReloadKey] = useState(0);
  const isMobile = useIsMobile(768);

  const formatCurrency = (amount: number, currency = 'AUD') =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(amount);

  const isOverdue = (inv: Invoice) => {
    if ((inv.status !== 'APPROVED' && inv.status !== 'SENT') || !inv.amountDue) return false;
    return isDateOverdue(inv.dueDate);
  };

  const handleVoid = async () => {
    if (!invoice) return;
    setActionError(null);
    try {
      await voidInvoice(invoice.id);
      onVoided?.();
      onHide();
    } catch (err: any) {
      setActionError(err?.message || 'Failed to void invoice');
    }
  };

  const handleEmail = async () => {
    if (!invoice) return;
    setActionError(null);
    try {
      const { to } = await emailInvoice(invoice.id);
      toast.success(`Drafted an email${to ? ` to ${to}` : ''} with the invoice attached — review & send it from your Drafts.`);
    } catch (err: any) {
      setActionError(err?.message || 'Failed to draft the invoice email');
    }
  };

  const handleCopyLink = async () => {
    if (!invoice) return;
    setActionError(null);
    setCopySuccess(false);
    try {
      const url = await getOnlineInvoiceUrl(invoice.id);
      if (url) {
        setOnlineUrl(url);
        await navigator.clipboard.writeText(url);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } else {
        setActionError('Online invoice URL not available');
      }
    } catch (err: any) {
      setActionError(err?.message || 'Failed to get link');
    }
  };

  if (!invoice) return null;

  const overdue = isOverdue(invoice);
  // PAID + VOIDED are frozen in the accounting layer; everything else
  // (DRAFT/SUBMITTED/AUTHORISED/APPROVED/SENT) is editable.
  const canEdit = invoice.status !== 'PAID' && invoice.status !== 'VOIDED';
  const canVoid = invoice.status === 'APPROVED' || invoice.status === 'SENT';
  const canEmail = invoice.status !== 'PAID' && invoice.status !== 'VOIDED';
  const canCopyLink = invoice.status === 'APPROVED' || invoice.status === 'PAID';
  const canRecordPayment = (invoice.status === 'APPROVED' || invoice.status === 'SENT') && (invoice.amountDue || 0) > 0;
  const isLoading = invoiceActionLoading === invoice.id;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" fullscreen="md-down">
      <Modal.Header closeButton className="border-0 pb-0">
        <div className="d-flex flex-column">
          <Modal.Title className="d-flex align-items-center gap-2">
            <span>Invoice {invoice.invoiceNumber}</span>
            {status && (
              <span className={`badge ${status.badgeClass}`} style={{ fontSize: '0.7rem' }}>
                <i className={`bi ${status.icon} me-1`} />
                {status.label}
              </span>
            )}
            {overdue && (
              <span className="badge badge-light-danger" style={{ fontSize: '0.7rem' }}>
                <i className="bi bi-exclamation-circle-fill me-1" />
                Overdue
              </span>
            )}
          </Modal.Title>
          <span className="text-muted fs-7 mt-1">{invoice.contactName || 'No customer'}</span>
        </div>
      </Modal.Header>

      <Modal.Body className="pt-4">
        {actionError && (
          <div className="alert alert-danger py-2 mb-4 d-flex align-items-center" style={{ fontSize: '0.8125rem' }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />
            {actionError}
          </div>
        )}

        {/* Amount summary card */}
        <div
          className="rounded-3 p-4 mb-4"
          style={{ background: 'var(--bs-tertiary-bg)' }}
        >
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <span className="text-muted fs-7 d-block mb-1">Total</span>
              <span className="fw-bold fs-3">{formatCurrency(invoice.total, invoice.currencyCode)}</span>
            </div>
            {(invoice.amountDue || 0) > 0 && (
              <div className="text-end">
                <span className="text-muted fs-7 d-block mb-1">Amount Due</span>
                <span className={`fw-bold fs-3 ${overdue ? 'text-danger' : 'text-warning'}`}>
                  {formatCurrency(invoice.amountDue, invoice.currencyCode)}
                </span>
              </div>
            )}
            {(invoice.amountDue || 0) === 0 && invoice.status === 'PAID' && (
              <div className="text-end">
                <span className="text-muted fs-7 d-block mb-1">Amount Due</span>
                <span className="fw-bold fs-3 text-success">
                  {formatCurrency(0, invoice.currencyCode)}
                </span>
              </div>
            )}
          </div>

          {/* Subtotal / Tax breakdown */}
          {(invoice.amountPaid || 0) > 0 && (
            <div className="d-flex gap-4 mt-3 pt-3 border-top" style={{ borderColor: 'var(--bs-border-color) !important' }}>
              <div>
                <span className="text-muted fs-8 d-block">Paid</span>
                <span className="fw-semibold fs-7 text-success">{formatCurrency(invoice.amountPaid || 0, invoice.currencyCode)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Details section */}
        <div className="d-flex flex-column gap-3">
          <div className="row g-3">
            <div className="col-sm-6">
              <span className="text-muted fs-8 d-block mb-1">Invoice Date</span>
              <span className="fw-semibold fs-7">{formatDateStr(invoice.date)}</span>
            </div>
            <div className="col-sm-6">
              <span className="text-muted fs-8 d-block mb-1">Due Date</span>
              <span className={`fw-semibold fs-7 ${overdue ? 'text-danger' : ''}`}>
                {formatDateStr(invoice.dueDate)}
                {overdue && <i className="bi bi-exclamation-circle-fill text-danger ms-2" />}
              </span>
            </div>
          </div>

          {invoice.reference && (
            <div>
              <span className="text-muted fs-8 d-block mb-1">Reference</span>
              <span className="fw-semibold fs-7">{invoice.reference}</span>
            </div>
          )}

          {(onlineUrl || copySuccess) && (
            <div>
              <span className="text-muted fs-8 d-block mb-1">Online Invoice Link</span>
              <div className="d-flex align-items-center gap-2">
                {onlineUrl && (
                  <a href={onlineUrl} target="_blank" rel="noopener noreferrer" className="small text-break text-primary">
                    {onlineUrl}
                  </a>
                )}
                {copySuccess && <span className="badge bg-success">Copied</span>}
              </div>
            </div>
          )}
        </div>

        {/* Line Items */}
        {invoice.lineItems && invoice.lineItems.length > 0 && (
          <div className="mt-4 pt-3 border-top">
            <span className="text-muted fs-8 d-block mb-2">Line Items</span>
            <div className="table-responsive">
              <table className="table table-sm table-row-bordered mb-0">
                <thead>
                  <tr className="fs-8 text-muted">
                    <th>Description</th>
                    <th className="text-end" style={{ width: '60px' }}>Qty</th>
                    <th className="text-end" style={{ width: '100px' }}>Unit Price</th>
                    <th className="text-end" style={{ width: '100px' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lineItems.map((item, idx) => (
                    <tr key={idx} className="fs-7">
                      <td>{item.description || '-'}</td>
                      <td className="text-end">{item.quantity || 1}</td>
                      <td className="text-end">{formatCurrency(item.unitAmount || 0, invoice.currencyCode)}</td>
                      <td className="text-end fw-semibold">{formatCurrency(item.lineAmount || 0, invoice.currencyCode)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payment History */}
        <PaymentHistory
          documentId={invoice.id}
          currencyCode={invoice.currencyCode}
          onPaymentDeleted={() => setShowRecordPayment(false)}
          fetchPayments={fetchPayments}
          deletePayment={deletePayment}
          paymentsLoading={paymentsLoading}
          deletePaymentLoading={deletePaymentLoading}
          reloadKey={paymentReloadKey}
        />

        {/* Quick actions bar: hamburger dropdown on mobile, buttons on desktop */}
        <div className="mt-4 pt-3 border-top">
          {isMobile ? (
            <Dropdown align="end" className="d-block">
              <Dropdown.Toggle
                variant="light"
                size="sm"
                className="d-flex align-items-center gap-2 invoice-view-actions-toggle"
                id="invoice-view-actions-dropdown"
              >
                <i className="bi bi-three-dots-vertical" />
                Actions
              </Dropdown.Toggle>
              <Dropdown.Menu className="invoice-view-actions-menu">
                {onEdit && canEdit && (
                  <Dropdown.Item
                    className="d-flex align-items-center gap-2 py-3"
                    onClick={() => onEdit(invoice)}
                    disabled={isLoading}
                  >
                    <i className="bi bi-pencil text-primary" />
                    Edit Invoice
                  </Dropdown.Item>
                )}
                {canRecordPayment && (
                  <Dropdown.Item
                    className="d-flex align-items-center gap-2 py-3"
                    onClick={() => setShowRecordPayment(true)}
                  >
                    <i className="bi bi-cash-coin text-success" />
                    Record Payment
                  </Dropdown.Item>
                )}
                {canEmail && (
                  <Dropdown.Item
                    className="d-flex align-items-center gap-2 py-3"
                    onClick={handleEmail}
                    disabled={isLoading}
                  >
                    {isLoading ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-envelope text-primary" />}
                    Email Invoice
                  </Dropdown.Item>
                )}
                {canCopyLink && (
                  <Dropdown.Item
                    className="d-flex align-items-center gap-2 py-3"
                    onClick={handleCopyLink}
                    disabled={isLoading}
                  >
                    {copySuccess ? <i className="bi bi-check-lg text-success" /> : <i className="bi bi-link-45deg text-info" />}
                    {copySuccess ? 'Link Copied!' : 'Copy Link'}
                  </Dropdown.Item>
                )}
                {onCopyToNew && (
                  <Dropdown.Item
                    className="d-flex align-items-center gap-2 py-3"
                    onClick={() => onCopyToNew(invoice)}
                    disabled={isLoading}
                  >
                    <i className="bi bi-files text-primary" />
                    Copy to New
                  </Dropdown.Item>
                )}
                {canVoid && (
                  <>
                    {(canRecordPayment || canEmail || canCopyLink) && <Dropdown.Divider />}
                    <Dropdown.Item
                      className="d-flex align-items-center gap-2 py-3 text-danger"
                      onClick={handleVoid}
                      disabled={isLoading}
                    >
                      {isLoading ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-x-circle" />}
                      Void Invoice
                    </Dropdown.Item>
                  </>
                )}
                {!canRecordPayment && !canEmail && !canCopyLink && !canVoid && !onCopyToNew && !(onEdit && canEdit) && (
                  <Dropdown.Item disabled className="py-3 text-muted">
                    No actions available
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {onEdit && canEdit && (
                <button
                  className="btn btn-light-primary btn-sm d-flex align-items-center gap-2"
                  onClick={() => onEdit(invoice)}
                  disabled={isLoading}
                  title="Edit this invoice"
                >
                  <i className="bi bi-pencil" />
                  Edit
                </button>
              )}
              {canRecordPayment && (
                <button
                  className="btn btn-success btn-sm d-flex align-items-center gap-2"
                  onClick={() => setShowRecordPayment(true)}
                >
                  <i className="bi bi-cash-coin" />
                  Record Payment
                </button>
              )}
              {canEmail && (
                <button
                  className="btn btn-light-primary btn-sm d-flex align-items-center gap-2"
                  onClick={handleEmail}
                  disabled={isLoading}
                >
                  {isLoading ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-envelope" />}
                  Email Invoice
                </button>
              )}
              {canCopyLink && (
                <button
                  className="btn btn-light-info btn-sm d-flex align-items-center gap-2"
                  onClick={handleCopyLink}
                  disabled={isLoading}
                >
                  {copySuccess ? <i className="bi bi-check-lg" /> : <i className="bi bi-link-45deg" />}
                  {copySuccess ? 'Link Copied!' : 'Copy Link'}
                </button>
              )}
              {onCopyToNew && (
                <button
                  className="btn btn-light-primary btn-sm d-flex align-items-center gap-2"
                  onClick={() => onCopyToNew(invoice)}
                  disabled={isLoading}
                  title="Duplicate this invoice as a new draft"
                >
                  <i className="bi bi-files" />
                  Copy to New
                </button>
              )}
              {canVoid && (
                <button
                  className="btn btn-light-danger btn-sm d-flex align-items-center gap-2 ms-auto"
                  onClick={handleVoid}
                  disabled={isLoading}
                >
                  {isLoading ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-x-circle" />}
                  Void Invoice
                </button>
              )}
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0 d-flex justify-content-between align-items-center">
        <button className="btn btn-light" onClick={onHide}>
          Close
        </button>
        <InvoicePdfActions
          invoiceId={invoice.id}
          invoiceNumber={invoice.invoiceNumber}
          size="sm"
        />
      </Modal.Footer>

      <RecordPaymentModal
        show={showRecordPayment}
        documentId={invoice.id}
        documentNumber={invoice.invoiceNumber}
        amountDue={invoice.amountDue || 0}
        currencyCode={invoice.currencyCode}
        onSuccess={() => setPaymentReloadKey((k) => k + 1)}
        onHide={() => setShowRecordPayment(false)}
        recordPayment={(p) => recordPayment({ invoiceId: p.documentId, method: p.method, amount: p.amount, date: p.date, reference: p.reference })}
        recordPaymentLoading={recordPaymentLoading}
      />
    </Modal>
  );
}

export default InvoiceViewModal;

